import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import FlexSelect from '../../../components/Inputs/FlexSelect';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory, useParams } from 'react-router-dom';
import Service from '../service';
import { toast } from 'react-toastify';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { useGetQueues } from 'views/User/form/hooks/useGetQueues';
import { useGetUsers } from 'views/Report/Audit/hooks/useGetUsers';
import { ScreenPrompt } from 'components/ScreenPrompt';
import TreeList from 'views/ChatDialogTree/form/tree/treeList';
import { useDialogTree } from 'views/ChatDialogTree/form/tree/context';
import DialogTreeModal from 'views/ChatDialogTree/form/components/DialogTreeModal';
import { AddActionButton, RemoveActionButton } from './styled';
import { FaRegTrashAlt } from 'react-icons/fa';

const ACTION_TYPE = [
  {
    label: 'Transferir para fila',
    value: 'TRANSFER_TO_QUEUE'
  },
  {
    label: 'Encerrar atendimento',
    value: 'CLOSE_CHAT'
  }
];

const FlexIAForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [channelType, setChannelType] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const queues = useGetQueues()?.queues ?? [];
  const queuesOptionList = queues.map((queue) => {
    return { value: queue.id, label: queue.name };
  });
  const users = useGetUsers()?.users ?? [];
  const userOptionList = users.map((user) => {
    return { value: user.login, label: user.name };
  });

  const {
    control,
    handleSubmit,
    errors,
    setValue,
    formState,
    clearErrors,
    reset,
    getValues
  } = useForm({
    shouldUnregister: false
  });

  const {
    fields: openAIActions,
    append: appendOpenAIAction,
    remove: removeOpenAIAction
  } = useFieldArray({
    control,
    name: 'actions'
  });

  const {
    isModalOpen,
    setIsModalOpen,
    onSubmitModalData,
    selectedNode,
    treeData,
    setTreeData,
    resetTree,
    validateTreeData
  } = useDialogTree();

  const customSetValue = useCallback(
    (fieldName, value) => {
      setValue(fieldName, value);
      clearErrors(fieldName);
    },
    [setValue, clearErrors]
  );

  const { isDirty } = formState;

  useEffect(() => {
    if (id) {
      setLoading(true);
      Service.getFlexiaById(id)
        .then((res) => {
          const flexiaType = res.data.data.type;
          const flexiaConfig = res.data.data.configuration;

          const data = {
            ...res.data.data,
            ...(flexiaConfig ?? {})
          };

          setChannelType(flexiaType);

          reset(data);
          if (flexiaType === 'DIALOG') {
            setTreeData(flexiaConfig);
          }

          setIsUpdate(true);
          setLoading(false);
        })
        .catch(() => {
          toast.error('Erro ao tentar carregar FlexIA', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    }
  }, [id, reset, setTreeData]);

  useEffect(() => {
    if (channelType === 'DIALOG' && !isUpdate) {
      resetTree();
    }
  }, [channelType, resetTree]);

  const formatData = (data) => {
    if (data.type === 'WATSON') {
      return {
        name: data.name,
        description: data.description,
        attendantLogin: data.attendantLogin,
        queueId: data.queueId,
        type: data.type,
        configuration: {
          apiKey: data.apiKey,
          assistantId: data.assistantId,
          workspaceId: data.workspaceId,
          ativado: data.ativado,
          queueOnError: data.queueOnError
        }
      };
    }

    if (data.type === 'OPENAI') {
      return {
        name: data.name,
        description: data.description,
        attendantLogin: data.attendantLogin,
        queueId: data.queueId,
        type: data.type,
        configuration: {
          apikey: data.apikey,
          assistantId: data.assistantId,
          projectId: data.projectId,
          instruction: data.instruction,
          actions: data.actions?.map((action) => {
            return {
              ...action,
              queueId: Number(action.queueId)
            };
          })
        }
      };
    }

    if (data.type === 'DIALOG') {
      return {
        name: data.name,
        description: data.description,
        attendantLogin: data.attendantLogin,
        queueId: data.queueId,
        type: data.type,
        configuration: treeData,
        isUsingGenerativeAI: data.isUsingGenerativeAI
      };
    }
  };

  const onSubmit = async (data) => {
    const dataDTO = formatData(data);
    if (validateTreeData() === undefined || data.type !== 'DIALOG') {
      const currentFormValues = getValues();
      try {
        setLoading(true);
        if (isUpdate) {
          await Service.putFlexiaById(id, dataDTO);
          toast.success(`FlexIA atualizada com sucesso`);
        } else {
          await Service.createFlexia(dataDTO);
          toast.success(`FlexIA cadastrada com sucesso`);
        }
      } catch (err) {
        reset(currentFormValues, { keepErrors: true });
        setChannelType(currentFormValues.type);

        toast.error(
          err.response?.data?.message ||
            `Ocorreu um erro ao ${isUpdate ? 'atualizar' : 'cadastrar'} flexIA`,
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
      } finally {
        if (data.type !== 'DIALOG') {
          history.push('/admin/flexia');
        } else {
          toast.info(
            'Árvore salva. Para retornar, clique na seta para voltar.',
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        }
        setLoading(false);
      }
    } else {
      toast.error(validateTreeData());
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/flexia');
  };

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/flexia"
        buttonTitle="Voltar para a listagem"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">FlexIA</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <>
                        <Row className="mb-2">
                          <Col md="6">
                            <label className="form-control-label">Nome*</label>
                            <Controller
                              as={Input}
                              control={control}
                              name="name"
                              defaultValue={''}
                              rules={{
                                required: 'Nome é um campo obrigatório'
                              }}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="name"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                          <Col md="6">
                            <label className="form-control-label">
                              Descrição*
                            </label>
                            <Controller
                              as={Input}
                              control={control}
                              name="description"
                              rules={{
                                required: 'Descrição é um campo obrigatório'
                              }}
                              defaultValue={''}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="description"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                        </Row>

                        <Row className="mb-2">
                          <Col md="6">
                            <label className="form-control-label">
                              Atendente Padrão*
                            </label>
                            <Controller
                              render={(props) => (
                                <FlexSelect
                                  isClearable={false}
                                  dataOptions={userOptionList ?? []}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName="attendantLogin"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              rules={{
                                required: 'Atendente é um campo obrigatório'
                              }}
                              control={control}
                              name="attendantLogin"
                              defaultValue=""
                            />

                            <ErrorMessage
                              errors={errors}
                              name="attendantLogin"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                          <Col md="6">
                            <label className="form-control-label">Fila*</label>
                            <Controller
                              render={(props) => (
                                <FlexSelect
                                  isClearable={false}
                                  dataOptions={queuesOptionList ?? []}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName="queueId"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              control={control}
                              name="queueId"
                              defaultValue=""
                              rules={{
                                required: 'Fila é um campo obrigatório'
                              }}
                            />

                            <ErrorMessage
                              errors={errors}
                              name="queueId"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md="6">
                            <label className="form-control-label">Tipo*</label>
                            <Controller
                              render={() => (
                                <FlexSelect
                                  isClearable={false}
                                  dataOptions={[
                                    { value: 'WATSON', label: 'Watson' },
                                    { value: 'OPENAI', label: 'OpenAI' },
                                    {
                                      value: 'DIALOG',
                                      label: 'Árvore de Diálogo'
                                    }
                                  ]}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={channelType}
                                  valueController={(name, value) => {
                                    setChannelType(value);
                                    setValue(name, value);
                                  }}
                                  fieldName="type"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              control={control}
                              name="type"
                              defaultValue={channelType}
                              rules={{
                                required: 'Tipo é um campo obrigatório'
                              }}
                            />

                            <ErrorMessage
                              errors={errors}
                              name="type"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                          {channelType === 'WATSON' && (
                            <Col md="6">
                              <label className="form-control-label">
                                Assistant ID*
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="assistantId"
                                rules={{
                                  required:
                                    'Assistant ID é um campo obrigatório'
                                }}
                                defaultValue={''}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="assistantId"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          )}
                          {channelType === 'OPENAI' && (
                            <Col md="6">
                              <label className="form-control-label">
                                Project API Key(Token)*
                              </label>

                              <Controller
                                as={Input}
                                control={control}
                                name="apikey"
                                rules={{
                                  required: 'Campo obrigatório'
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="apikey"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          )}
                        </Row>
                        {channelType === 'WATSON' && (
                          <>
                            <Row className="mb-2">
                              <Col md="6">
                                <label className="form-control-label">
                                  Api Key*
                                </label>
                                <Controller
                                  as={Input}
                                  control={control}
                                  name="apiKey"
                                  rules={{
                                    required: 'Api Key é um campo obrigatório'
                                  }}
                                  defaultValue={''}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="apiKey"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                              <Col md="6">
                                <label className="form-control-label">
                                  WorkSpace id
                                </label>
                                <Controller
                                  as={Input}
                                  control={control}
                                  name="workspaceId"
                                  defaultValue={''}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="workspaceId"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col md="6">
                                <label className="form-control-label">
                                  Status do Canal
                                </label>
                                <Controller
                                  render={(props) => (
                                    <FlexSelect
                                      isClearable={false}
                                      dataOptions={[
                                        { value: true, label: 'Habilitado' },
                                        { value: false, label: 'Desabilitado' }
                                      ]}
                                      isMulti={false}
                                      closeMenuOnSelect={true}
                                      value={props.value}
                                      valueController={setValue}
                                      fieldName="ativado"
                                      labelName="label"
                                      valueName="value"
                                    />
                                  )}
                                  control={control}
                                  name="ativado"
                                  defaultValue={true}
                                />

                                <ErrorMessage
                                  errors={errors}
                                  name="ativado"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                              <Col md="6">
                                <label className="form-control-label">
                                  Fila em caso de falha
                                </label>
                                <Controller
                                  render={(props) => (
                                    <FlexSelect
                                      isClearable={false}
                                      dataOptions={queuesOptionList ?? []}
                                      isMulti={false}
                                      closeMenuOnSelect={true}
                                      value={props.value}
                                      valueController={setValue}
                                      fieldName="queueOnError"
                                      labelName="label"
                                      rules={{
                                        required: 'Fila é um campo obrigatório'
                                      }}
                                      valueName="value"
                                    />
                                  )}
                                  control={control}
                                  name="queueOnError"
                                  defaultValue=""
                                />

                                <ErrorMessage
                                  errors={errors}
                                  name="queueOnError"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                            </Row>
                          </>
                        )}
                        {channelType === 'OPENAI' && (
                          <>
                            <Row className="mb-2">
                              <Col md="6">
                                <label className="form-control-label">
                                  Project Id*
                                </label>

                                <Controller
                                  as={Input}
                                  control={control}
                                  name="projectId"
                                  rules={{
                                    required: 'Campo obrigatório'
                                  }}
                                  defaultValue=""
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="projectId"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                              <Col md="6">
                                <label className="form-control-label">
                                  Assistant Id*
                                </label>

                                <Controller
                                  as={Input}
                                  control={control}
                                  name="assistantId"
                                  rules={{
                                    required: 'Campo obrigatório'
                                  }}
                                  defaultValue=""
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="assistantId"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col md="6">
                                <label className="form-control-label">
                                  Intruções*
                                </label>

                                <Controller
                                  as={Input}
                                  type="textarea"
                                  rows="8"
                                  control={control}
                                  name="instruction"
                                  rules={{
                                    required: 'Campo obrigatório'
                                  }}
                                  defaultValue=""
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="instruction"
                                  render={({ message }) =>
                                    errorFormMessage(message)
                                  }
                                />
                              </Col>
                            </Row>
                            <h4
                              className="mt-0 mb-0"
                              style={{
                                paddingTop: '2rem'
                              }}
                            >
                              Ações Inteligentes
                            </h4>
                            <hr className="mt-2 mb-4" />
                            {openAIActions.map((action, index) => (
                              <Row key={action.id} className="mb-3">
                                <Col md="3">
                                  <label className="form-control-label">
                                    Ação*
                                  </label>
                                  <Controller
                                    render={(props) => {
                                      return (
                                        <FlexSelect
                                          dataOptions={ACTION_TYPE}
                                          closeMenuOnSelect={true}
                                          value={props.value}
                                          valueController={(
                                            fieldName,
                                            value
                                          ) => {
                                            customSetValue(fieldName, value);
                                            action.type = value;
                                          }}
                                          fieldName={props.name}
                                          placeholder="Ação"
                                          labelName="label"
                                          valueName="value"
                                        />
                                      );
                                    }}
                                    control={control}
                                    name={`actions.${index}.type`}
                                    rules={{
                                      required: 'Selecione uma ação'
                                    }}
                                    defaultValue={action.type}
                                  />
                                  <ErrorMessage
                                    errors={errors}
                                    name={`actions.${index}.type`}
                                    render={({ message }) =>
                                      errorFormMessage(message)
                                    }
                                  />
                                </Col>
                                {action.type === 'TRANSFER_TO_QUEUE' && (
                                  <Col md="2">
                                    <label className="form-control-label">
                                      Fila*
                                    </label>
                                    <Controller
                                      as={
                                        <Input type="select">
                                          <option value="">
                                            Selecione uma fila
                                          </option>
                                          {queues.map((queues, index) => (
                                            <option
                                              key={index}
                                              value={queues.id}
                                            >
                                              {queues.name}
                                            </option>
                                          ))}
                                        </Input>
                                      }
                                      control={control}
                                      name={`actions.${index}.queueId`}
                                      rules={{
                                        required: 'Este campo é obrigatório'
                                      }}
                                      defaultValue={action.queueId || ''}
                                    />
                                    <ErrorMessage
                                      errors={errors}
                                      name={`actions.${index}.queueId`}
                                      render={({ message }) =>
                                        errorFormMessage(message)
                                      }
                                    />
                                  </Col>
                                )}
                                <Col className="col">
                                  <label className="form-control-label">
                                    Instruções*
                                  </label>
                                  <Controller
                                    as={Input}
                                    control={control}
                                    name={`actions.${index}.id`}
                                    defaultValue={action.id}
                                    hidden
                                    disabled
                                  />

                                  <Controller
                                    as={Input}
                                    control={control}
                                    name={`actions.${index}.instruction`}
                                    rules={{
                                      required:
                                        'Uma instrução para a ação é obrigatória'
                                    }}
                                    defaultValue={action.instruction}
                                    placeholder={
                                      action.type === 'CLOSE_CHAT'
                                        ? 'Exemplo: "Execute quando entender que o cliente já teve a demanda solucionada."'
                                        : 'Exemplo: "Execute quando o cliente relatar problema X."'
                                    }
                                  />
                                  <ErrorMessage
                                    errors={errors}
                                    name={`actions.${index}.instruction`}
                                    render={({ message }) =>
                                      errorFormMessage(message)
                                    }
                                  />
                                </Col>
                                <Col className="col-auto d-flex">
                                  <RemoveActionButton
                                    type="button"
                                    onClick={async () => {
                                      removeOpenAIAction(index);
                                    }}
                                  >
                                    <FaRegTrashAlt size={20} />
                                  </RemoveActionButton>
                                </Col>
                              </Row>
                            ))}

                            <Row>
                              <Col md="12">
                                <AddActionButton
                                  type="button"
                                  onClick={() =>
                                    appendOpenAIAction({
                                      name: '',
                                      new: true,
                                      type: '',
                                      instruction: '',
                                      queueId: ''
                                    })
                                  }
                                >
                                  + Adicionar ação
                                </AddActionButton>
                              </Col>
                            </Row>
                          </>
                        )}
                        {channelType === 'DIALOG' && (
                          <Row className="mb-3">
                            <Col sm="4" className="ml-4">
                              <Controller
                                name="isUsingGenerativeAI"
                                control={control}
                                defaultValue={false}
                                render={(props) => (
                                  <>
                                    <label className="form-control-label">
                                      <Input
                                        type="checkbox"
                                        onChange={(e) =>
                                          props.onChange(e.target.checked)
                                        }
                                        checked={props.value}
                                      />
                                      Usar IA Generativa
                                    </label>
                                  </>
                                )}
                              />
                            </Col>
                          </Row>
                        )}
                      </>

                      <hr />

                      <ReactButton btnColor="confirmation" type="submit">
                        Salvar
                      </ReactButton>
                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                      {channelType === 'DIALOG' && (
                        <>
                          <hr />
                          <Row className="mb-2">
                            <TreeList />
                          </Row>
                        </>
                      )}
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
      <DialogTreeModal
        isOpenModal={isModalOpen}
        onModalToggle={setIsModalOpen}
        onSubmitModalData={onSubmitModalData}
        selectedNode={selectedNode}
        flexiaId={id}
      />
    </>
  );
};

export default FlexIAForm;
